import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, Match, Show, Switch } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { FileUpload, FileUploadContext } from "@ark-ui/solid";
import { TextAndIconButton, TextButton } from "../button/Button";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import { useTranslation } from "../../state/i18n";
import { isEmpty, isNil } from "lodash-es";
import { MoreAppError } from "../../MoreAppError";
import type { PendingCustomerResource } from "../../services/CustomerResourceService";
import logger from "../../utils/logger";
import type { FileMimeType } from "@zag-js/file-utils";
import { type FileUploadError, toCustomerFile, translateFileUploadError } from "../../utils/uploadUtil";
import Text from "../text/Text";

type FileInputProps = {
  value?: {
    filename: string;
    mediaTypeIcon: IconName;
  };
  accept: FileMimeType[];
  maxFileSizeInBytes: number;
  uploadActionLabel: string;
  onFileSelected: (file: PendingCustomerResource) => Promise<void>;
  onUploadCancel: () => void;
  downloadFile: () => void;
} & ExtendableInputProps;

const FileInput: VoidComponent<FileInputProps> = (props) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = createSignal<FileUploadError>();
  const [uploadingFile, setUploadingFile] = createSignal<PendingCustomerResource>();

  const errorMessage = createMemo(() => {
    const error = uploadError();
    return !isNil(error) ? translateFileUploadError(error, props.maxFileSizeInBytes) : props.errorMessage;
  });

  const uploadFile = async (files: File[]) => {
    const uploadFile = files[0];
    if (isNil(uploadFile)) {
      return;
    }

    const customerFile = toCustomerFile(uploadFile);

    try {
      setUploadError();
      setUploadingFile(customerFile);
      await props.onFileSelected(customerFile);
    } catch (e) {
      if (e instanceof MoreAppError && e.code === "REQUEST_ABORTED") {
        setUploadError("UPLOAD_ABORTED");
      } else {
        setUploadError("UPLOAD_ERROR");
        logger.error("Failed to upload file", e);
      }
    } finally {
      setUploadingFile(undefined);
    }
  };

  return (
    <Input {...props} errorMessage={errorMessage()}>
      {(id) => (
        <FileUpload.Root
          id={id}
          class="relative"
          maxFiles={1}
          maxFileSize={props.maxFileSizeInBytes}
          accept={props.accept}
          onFileAccept={(details) => uploadFile(details.files)}
          onFileReject={(details) => {
            if (!isEmpty(details.files) && !isEmpty(details.files[0].errors)) {
              // Only one error can be shown, so pick the first one
              setUploadError(details.files[0].errors[0]);
            }
          }}
        >
          <FileUploadContext>
            {(context) => (
              <Show
                when={!isNil(props.value) || !isNil(uploadingFile())}
                fallback={
                  <FileUpload.Trigger
                    asChild={(triggerProps) => (
                      <TextAndIconButton
                        block
                        variant="secondary"
                        icon="upload"
                        text={props.uploadActionLabel}
                        {...triggerProps}
                      />
                    )}
                  />
                }
              >
                <div
                  class="flex items-center gap-1.5 rounded-sm border bg-surface-extra-subtle py-2 pr-2 pl-3"
                  classList={{
                    "border-subtle": !isNil(errorMessage),
                    "border-error": isNil(errorMessage),
                  }}
                >
                  <Switch>
                    <Match when={uploadingFile()}>
                      {(uploadingFile) => (
                        <>
                          <Icon name="spinner" size="lg" type="solid" class="animate-spin icon-secondary" />
                          <Text
                            class="text-base-regular text-primary"
                            text={`${t(($) => $.FILE_INPUT_UPLOADING)} ${uploadingFile().fileName}`}
                            truncate
                          />
                          <div class="ml-auto">
                            <TextButton
                              variant="secondary"
                              text={t(($) => $.FILE_INPUT_ACTION_CANCEL_UPLOAD)}
                              onClick={props.onUploadCancel}
                            />
                          </div>
                        </>
                      )}
                    </Match>
                    <Match when={props.value}>
                      {(value) => (
                        <>
                          <Icon class="flex-none" name={value().mediaTypeIcon} type="solid" size="lg" />
                          <Text class="text-base-regular text-primary" text={value().filename} truncate />
                          <div class="ml-auto flex gap-1">
                            <TextButton
                              variant="secondary"
                              text={t(($) => $.FILE_INPUT_ACTION_DOWNLOAD)}
                              onClick={props.downloadFile}
                            />
                            <TextButton
                              variant="tertiary"
                              text={t(($) => $.FILE_INPUT_ACTION_CHANGE_FILE)}
                              onClick={() => context().openFilePicker()}
                            />
                          </div>
                        </>
                      )}
                    </Match>
                  </Switch>
                </div>
              </Show>
            )}
          </FileUploadContext>
          <FileUpload.HiddenInput data-testid="fileInput" />
        </FileUpload.Root>
      )}
    </Input>
  );
};

export default FileInput;
